import type { TFunction } from 'i18next'

import type { AmsTrendGranularity, AmsTrendMetric } from '@/client'
import {
  addDays,
  isoDay,
  shiftMonths,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  type DateRangePreset,
} from '@/lib/dates'
import { formatDayMonth, formatMonthYearShort } from '@/lib/format'

/**
 * The catalogues and pure rules behind AMS View's trend charts — the per-row
 * chart here and the page chart in `list/OverallTrendChart` (VentaHub
 * `pages/ams_view/components/TrendChart.tsx`, whose helpers the overall chart
 * imported too). The Hourly granularity, its presets and the attribution
 * window are not ported: hourly data is hidden (decision 2).
 */

/** Metrics that share a Y-axis: money in the row's currency, or plain counts (`TrendChart.tsx:136-139`). */
export type MetricUnit = 'currency' | 'count'

/** The five metrics, in the menu's order (`TrendChart.tsx:141-152`). */
export const TREND_METRICS = [
  'spend',
  'sales',
  'impressions',
  'clicks',
  'units',
] as const satisfies readonly AmsTrendMetric[]

/** Each metric's axis (`TrendChart.tsx:147-151`). */
export const METRIC_UNIT: Record<AmsTrendMetric, MetricUnit> = {
  spend: 'currency',
  sales: 'currency',
  impressions: 'count',
  clicks: 'count',
  units: 'count',
}

/** The menu's two sections, one per axis (`TrendChart.tsx:165-180`). */
export const METRIC_GROUPS: ReadonlyArray<{ unit: MetricUnit; metrics: AmsTrendMetric[] }> = [
  { unit: 'currency', metrics: TREND_METRICS.filter((m) => METRIC_UNIT[m] === 'currency') },
  { unit: 'count', metrics: TREND_METRICS.filter((m) => METRIC_UNIT[m] === 'count') },
]

/** Bucket sizes, Hourly removed (`TrendChart.tsx:182-189`). */
export const TREND_GRANULARITIES = [
  'daily',
  'weekly',
  'monthly',
  'quarterly',
  'yearly',
] as const satisfies readonly AmsTrendGranularity[]

/** What a chart plots before anyone picks (`TrendChart.tsx:346-347`). */
export const DEFAULT_TREND_METRICS: readonly AmsTrendMetric[] = ['spend']
export const DEFAULT_TREND_GRANULARITY: AmsTrendGranularity = 'daily'

/**
 * Each metric's colour, its place in the chart ramp — fixed, as VentaHub's
 * were (`TrendChart.tsx:141-152`), so Spend is the same colour on every chart
 * and the menu can show every metric's swatch before it is picked.
 */
export const METRIC_COLOR: Record<AmsTrendMetric, number> = {
  spend: 0,
  sales: 1,
  impressions: 2,
  clicks: 3,
  units: 4,
}

/**
 * The selection after a metric is ticked or unticked
 * (`TrendChart.tsx:531-539`, `OverallTrendChart.tsx:612-616`): never empty —
 * unticking the last metric keeps it — and never two axes: the newest pick's
 * unit wins and metrics of the other unit drop out. The page's URL applies
 * the same rule to a link (`search.ts`), so the page chart and a row's chart
 * answer the same click the same way.
 */
export function nextMetricSelection(
  current: readonly AmsTrendMetric[],
  toggled: AmsTrendMetric,
): AmsTrendMetric[] {
  const next = current.includes(toggled)
    ? current.filter((metric) => metric !== toggled)
    : [...current, toggled]
  const newest = next.at(-1)
  if (newest === undefined) return [...current]
  return next.filter((metric) => METRIC_UNIT[metric] === METRIC_UNIT[newest])
}

function preset(id: string, label: string, from: string, today: string): DateRangePreset {
  return { id, label, range: { from, to: today } }
}

/**
 * The range chips for one granularity (`TrendChart.tsx:224-270`). Every range
 * ends today. VentaHub subtracted from "now" — `subDays(now, 7)` — so "Last 7
 * days" here starts seven days back and spans eight, unlike the page's own
 * chip; that arithmetic is kept.
 */
export function trendPresets(
  granularity: AmsTrendGranularity,
  today: Date,
  t: TFunction,
): DateRangePreset[] {
  const day = isoDay(today)
  const p = (key: string, label: string, from: string) =>
    preset(`${granularity}-${key}`, label, from, day)
  switch (granularity) {
    case 'daily':
      return [
        p('7', t('amsView.trend.presets.lastDays', { count: 7 }), addDays(day, -7)),
        p('30', t('amsView.trend.presets.lastDays', { count: 30 }), addDays(day, -30)),
        p('90', t('amsView.trend.presets.lastDays', { count: 90 }), addDays(day, -90)),
        p('month', t('amsView.trend.presets.thisMonth'), startOfMonth(day)),
      ]
    case 'weekly':
      return [
        p('4', t('amsView.trend.presets.lastWeeks', { count: 4 }), addDays(day, -28)),
        p('12', t('amsView.trend.presets.lastWeeks', { count: 12 }), addDays(day, -84)),
        p('26', t('amsView.trend.presets.lastWeeks', { count: 26 }), addDays(day, -182)),
        p('ytd', t('amsView.trend.presets.yearToDate'), startOfYear(day)),
      ]
    case 'monthly':
      return [
        p('3', t('amsView.trend.presets.lastMonths', { count: 3 }), shiftMonths(day, -3)),
        p('6', t('amsView.trend.presets.lastMonths', { count: 6 }), shiftMonths(day, -6)),
        p('12', t('amsView.trend.presets.lastMonths', { count: 12 }), shiftMonths(day, -12)),
        p('ytd', t('amsView.trend.presets.yearToDate'), startOfYear(day)),
      ]
    case 'quarterly':
      return [
        p('2', t('amsView.trend.presets.lastQuarters', { count: 2 }), shiftMonths(day, -6)),
        p('4', t('amsView.trend.presets.lastQuarters', { count: 4 }), shiftMonths(day, -12)),
        p('8', t('amsView.trend.presets.lastQuarters', { count: 8 }), shiftMonths(day, -24)),
        p('quarter', t('amsView.trend.presets.thisQuarter'), startOfQuarter(day)),
      ]
    case 'yearly':
      return [
        p('2', t('amsView.trend.presets.lastYears', { count: 2 }), shiftMonths(day, -24)),
        p('5', t('amsView.trend.presets.lastYears', { count: 5 }), shiftMonths(day, -60)),
        p('10', t('amsView.trend.presets.lastYears', { count: 10 }), shiftMonths(day, -120)),
        p('year', t('amsView.trend.presets.thisYear'), startOfYear(day)),
      ]
  }
}

/**
 * A bucket's label on the x axis and in the hover text (`TrendChart.tsx:303-330`):
 * years and quarters (`YYYY-Qn`) as the API sends them, months as "Oct '26",
 * days and weeks (the ISO Monday) as "5 Oct".
 */
export function formatTrendPeriod(
  period: string,
  granularity: AmsTrendGranularity,
  lang = 'en',
): string {
  if (granularity === 'yearly' || granularity === 'quarterly') return period
  // TrendChart.tsx:310-315 (`MMM ''yy`) and :326-329 (`d MMM`); built from the
  // day's parts, never `new Date(day)`, which labels the day before west of UTC.
  if (granularity === 'monthly') return formatMonthYearShort(period, lang)
  return formatDayMonth(period, lang)
}
